import { useTranslation } from "react-i18next";
import {
  AutoComplete,
  FormContainer,
  ISelectType,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { useCallback, useEffect, useState } from "react";
import { Grid } from "@mui/material";
import { ICancelationReasonsProps } from "./models";
import {
  calendarConsultationCancelationReasonList,
  calendarExamCancelationReasonList,
  createTicketAutomationReceivedRuleCancelationReason,
  getTicketAutomationReceivedRuleCancelationReasonById,
  updateTicketAutomationReceivedRuleCancelationReasonById,
} from "../../../../../../../services/ticketAutomationReceivedRuleCancelationReason.service";
import {
  cancelationReasonsSchema,
  TCancelationReasonsForm,
} from "./cancelationReasonsSchema";

const translationPath =
  "chatBot.page.automations.automationReceivedRules.automationReceivedRuleCancelationReason.";

const CancelationReasons = ({
  id,
  ticketAutomationReceivedRuleId,
  automationCode,
  onBackButton,
}: ICancelationReasonsProps) => {
  const { t } = useTranslation();

  const { sendRequest: save, loading: sLoading } = useFetch(
    createTicketAutomationReceivedRuleCancelationReason,
  );

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateTicketAutomationReceivedRuleCancelationReasonById,
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getTicketAutomationReceivedRuleCancelationReasonById,
  );

  const updateHandler = (v: TCancelationReasonsForm, id: string) =>
    update({
      item: {
        id,
        ...v,
        externalId: v.cancelationReason.id,
        externalName: v.cancelationReason.name,
        ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
      },
      id,
    });

  const saveHandler = (v: TCancelationReasonsForm) =>
    save({
      ...v,
      externalId: v.cancelationReason.id,
      externalName: v.cancelationReason.name,
      ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
    });

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TCancelationReasonsForm>> => {
      const { data, success } = await get(id);

      if (data) {
        const newData: TCancelationReasonsForm = {
          ...data,
          cancelationReason: {
            id: data.externalId,
            name: data.externalName,
          },
        };
        return { data: newData, success: true };
      }
      return { data, success };
    },
    [get],
  );

  const [optionsCancelationReasons, setOptionsCancelationReasons] = useState<
    ISelectType[]
  >([]);

  const { sendRequest: sendExamRequest } = useFetch(
    calendarExamCancelationReasonList,
  );
  const { sendRequest: sendConsultationRequest } = useFetch(
    calendarConsultationCancelationReasonList,
  );

  const fetchCancelationReasons = useCallback(async () => {
    const request =
      automationCode === "21" ? sendExamRequest : sendConsultationRequest;

    const { data, success } = await request(undefined);
    if (data && success) {
      setOptionsCancelationReasons(
        data.map((reason) => ({
          id: reason.cancellationReasonCode,
          name: reason.cancellationReasonName,
        })),
      );
    }
  }, [automationCode, sendConsultationRequest, sendExamRequest]);

  useEffect(() => {
    fetchCancelationReasons();
  }, [fetchCancelationReasons]);

  const loading = sLoading || uLoading || gLoading;

  return (
    <FormContainer<TCancelationReasonsForm>
      saveFunction={saveHandler}
      updateFunction={updateHandler}
      getFunction={getHandler}
      loading={loading}
      title={t(translationPath + "cancelation_reason")}
      schema={cancelationReasonsSchema}
      subtitle={t(translationPath + "new_cancelation_reason")}
      backButtonOnClick={onBackButton}
      redirectOnSave={false}
      onSuccess={onBackButton}
      isModalForm={true}
      propId={id}
      formActionsOnBottom={false}
      fields={() => (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <AutoComplete
              fullWidth
              options={optionsCancelationReasons}
              name="cancelationReason"
              label={t(translationPath + "cancelation_reason_field")}
              getOptionLabel={(option) => option.name}
              size="small"
            />
          </Grid>
        </Grid>
      )}
    />
  );
};

export default CancelationReasons;
